import { DatePipe, JsonPipe } from "@angular/common";
import { Component, ElementRef, signal, ViewChild } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { MatButtonModule } from "@angular/material/button";
import { MatIconModule } from "@angular/material/icon";
import { MatProgressSpinnerModule } from "@angular/material/progress-spinner";
import { MatTooltipModule } from "@angular/material/tooltip";
import { IApiResponse } from "@models/api-response.model";
import { RequestService } from "@services/index";

interface ChatToolCall {
  name: string;
  arguments: Record<string, unknown>;
  result: unknown;
  error?: string;
}

/** A flat array of objects rendered as a table — one per collection a tool result returns. */
interface TableView {
  title: string;
  rows: Record<string, unknown>[];
  columns: string[];
}

/** get_order gets its own layout — a header card plus a line-item table. */
interface OrderView {
  kind: "order";
  order: Record<string, any>;
  items: Record<string, any>[];
  note?: string;
}
interface TablesView {
  kind: "tables";
  tables: TableView[];
}
type ToolView = OrderView | TablesView | { kind: "none" };

interface ChatMessage {
  role: "user" | "assistant";
  content: string;
  toolCalls?: ChatToolCall[];
  /** Structured renderings of toolCalls' results, computed once when the message arrives. */
  views?: ToolView[];
  /** Shows the raw tool-call trace (args, errors) for this message. */
  showTrace?: boolean;
}

const COLUMN_LIMIT = 8;

/**
 * A chat with an LLM that can look up orders, customers, vendors, and products —
 * read-only, ops-facing. History lives only in this component's memory: a reload
 * starts a clean session, and nothing sent here is logged server-side.
 */
@Component({
  selector: "fh-fishyops-ai",
  templateUrl: "./fishyops-ai.component.html",
  styleUrls: ["./fishyops-ai.component.scss"],
  standalone: true,
  imports: [
    DatePipe,
    FormsModule,
    JsonPipe,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
  ],
})
export class FishyOpsAiComponent {
  @ViewChild("scrollAnchor") private scrollAnchor?: ElementRef<HTMLDivElement>;
  @ViewChild("composerInput") private composerInput?: ElementRef<HTMLTextAreaElement>;

  messages = signal<ChatMessage[]>([]);
  draft = "";
  sending = signal(false);
  error = signal<string>("");
  copiedIndex = signal<number>(-1);

  readonly examples = [
    "Who sells Angelfish right now?",
    "Which customer belongs to order KS9ZTUP4G3MX?",
    "Is order KS9ZTUP4G3MX fully delivered?",
    "What is the email for customer John Tan?",
  ];

  constructor(private requestService: RequestService) {}

  useExample(q: string) {
    this.draft = q;
    this.send();
  }

  send() {
    const text = this.draft.trim();
    if (!text || this.sending()) return;
    this.draft = "";
    this.error.set("");
    this.messages.update((m) => [...m, { role: "user", content: text }]);
    this.sending.set(true);
    this.scrollSoon();

    const history = this.messages().map((m) => ({ role: m.role, content: m.content }));
    this.requestService
      .post<IApiResponse>("/api/v2/fishyops-ai/chat", { messages: history })
      .subscribe({
        next: (res) => {
          this.sending.set(false);
          if (!res.status || !res.data) {
            this.error.set(res.message || "FishyOps AI could not answer that.");
            return;
          }
          const data = res.data as { reply: string; toolCalls: ChatToolCall[] };
          const toolCalls = data.toolCalls || [];
          const views = toolCalls.map((t) => this.buildView(t)).filter((v) => v.kind !== "none");
          this.messages.update((m) => [
            ...m,
            { role: "assistant", content: data.reply, toolCalls, views },
          ]);
          this.scrollSoon();
        },
        error: () => {
          this.sending.set(false);
          this.error.set("FishyOps AI is unavailable right now — try again in a moment.");
        },
      });
  }

  onKeydown(event: KeyboardEvent) {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      this.send();
    }
  }

  toggleTrace(m: ChatMessage) {
    m.showTrace = !m.showTrace;
    this.messages.update((arr) => [...arr]);
  }

  clear() {
    this.messages.set([]);
    this.error.set("");
    setTimeout(() => this.composerInput?.nativeElement.focus(), 0);
  }

  copy(text: string, index: number) {
    navigator.clipboard?.writeText(text).then(() => {
      this.copiedIndex.set(index);
      setTimeout(() => this.copiedIndex.set(-1), 1200);
    });
  }

  /** A short label for a table's row, e.g. "3 rows" or "1 row". */
  rowCount(t: TableView): string {
    return `${t.rows.length} row${t.rows.length === 1 ? "" : "s"}`;
  }

  cell(row: Record<string, unknown>, col: string): string {
    const v = row[col];
    if (v === null || v === undefined || v === "") return "—";
    if (typeof v === "object") return JSON.stringify(v);
    return String(v);
  }

  private scrollSoon() {
    setTimeout(() => this.scrollAnchor?.nativeElement.scrollIntoView({ behavior: "smooth" }), 50);
  }

  /** Builds one array's worth of rows into a TableView, capping columns so wide rows don't blow out the layout. */
  private toTable(title: string, rows: unknown): TableView | null {
    if (!Array.isArray(rows) || !rows.length) return null;
    const columns = Object.keys(rows[0] as object).slice(0, COLUMN_LIMIT);
    return { title, rows: rows as Record<string, unknown>[], columns };
  }

  /** Turns one tool call's raw JSON result into a renderable card/table, or {kind:'none'} if there's nothing to show. */
  private buildView(t: ChatToolCall): ToolView {
    if (t.error || !t.result) return { kind: "none" };
    const r = t.result as any;

    switch (t.name) {
      case "get_order": {
        if (!r.order) return { kind: "none" };
        return {
          kind: "order",
          order: r.order,
          items: Array.isArray(r.items) ? r.items : [],
          note: r.note,
        };
      }
      case "find_customer": {
        const table = this.toTable("Customers", r);
        return table ? { kind: "tables", tables: [table] } : { kind: "none" };
      }
      case "find_vendor_for_product": {
        const tables = [
          this.toTable("Current listings", r.currentListings),
          this.toTable("Past order vendors", r.pastOrderVendors),
          this.toTable("Algolia suggestions", r.algoliaSuggestions),
        ].filter((t): t is TableView => t !== null);
        return tables.length ? { kind: "tables", tables } : { kind: "none" };
      }
      case "search_products": {
        const table = this.toTable("Search results", r);
        return table ? { kind: "tables", tables: [table] } : { kind: "none" };
      }
      case "run_read_only_query": {
        const table = this.toTable("Query results", r);
        return table ? { kind: "tables", tables: [table] } : { kind: "none" };
      }
      default:
        return { kind: "none" };
    }
  }
}
